import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogTitle, Divider, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme,
} from '@mui/material';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import Axios from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSnackbar } from 'notistack';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import { currentTask, taskBadgeColor } from '../../adminDashboard/missedCalls/missedCallFormat';

dayjs.extend(utc);
dayjs.extend(timezone);

const NY = 'America/New_York';
// Reminder emails go out at 22:30 New York: the first on the day the task is requested, the second a week later.
const firstReminderAt = (requestedAt) => dayjs(requestedAt).tz(NY).hour(22).minute(30).second(0).format('YYYY-MM-DD HH:mm');
const secondReminderAt = (requestedAt) => dayjs(requestedAt).tz(NY).add(7, 'day').hour(22).minute(30).second(0).format('YYYY-MM-DD HH:mm');
const nyTime = (value, fmt = 'YYYY-MM-DD hh:mm A') => dayjs(value).tz(NY).format(fmt);

const Line = ({ label, children, color, italic }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ minWidth: 120, opacity: 0.7 }}>{label}</Typography>
        <Typography variant="h6" color={color} sx={{ fontStyle: italic ? 'italic' : 'normal' }}>{children}</Typography>
    </Box>
);

/**
 * The memo task badge — 신규문의 red, 재문의 orange, none / done grey — and its dialog: create a task, assign it to
 * me or unassign, mark it complete, or find and apply a rollback. Same routes, colors and steps as the badge on
 * the consultation card (adminDashboard/consultationProgress/ConsultationCard.jsx, which keeps its own inline
 * copies for now). Tasks made here behave like any other task, parent reminder emails included.
 *
 * Props: studentUuid · memo ({ id, tasks }) · currentUser (the admin's full name — tasks record claims by name) ·
 * onChanged (after any change) · disabled.
 */
const MemoTaskBadge = ({ studentUuid, memo, currentUser, onChanged, disabled = false }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = useState(false);
    const [confirmComplete, setConfirmComplete] = useState(false);
    const [confirmResend, setConfirmResend] = useState(false);   // a second 신규문의 for a family that already got the mail
    const [rollbackTask, setRollbackTask] = useState(null);
    const [busy, setBusy] = useState(false);
    // 신규문의 or 재문의 — asked only of a potential student, and answered in advance from their record.
    const [status, setStatus] = useState(null);      // { is_potential_student, has_had_info_mail, has_open_info_mail }
    const [kind, setKind] = useState(null);          // 'new' | 'repeat'

    const tasks = Array.isArray(memo?.tasks) ? memo.tasks : [];
    const task = currentTask(tasks);
    const openTask = task && !task.is_completed ? task : null;
    const base = `${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/memos/${memo?.id}/tasks`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const close = () => { setOpen(false); setRollbackTask(null); setConfirmComplete(false); setConfirmResend(false); };

    // Read it fresh each time the dialog opens: another admin may have sent the mail since the page loaded.
    useEffect(() => {
        if (!open || !studentUuid) return undefined;
        let live = true;
        setStatus(null);
        setKind(null);
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/info-mail-status`, auth)
            .then((res) => {
                if (!live) return;
                const st = res.data || {};
                setStatus(st);
                // The first 안내 메일 goes out once; after that a new call is a 재문의.
                setKind(st.is_potential_student && !st.has_had_info_mail && !st.has_open_info_mail ? 'new' : 'repeat');
            })
            .catch(() => { if (live) { setStatus({}); setKind('repeat'); } });
        return () => { live = false; };
    }, [open, studentUuid]);   // eslint-disable-line react-hooks/exhaustive-deps

    const askKind = Boolean(status?.is_potential_student);
    const hintFor = () => {
        if (!status) return '학생 정보를 확인하는 중…';
        if (!askKind) return '등록된 학생이라 신규문의가 아니라 재문의 태스크로 만들어집니다.';
        if (status.has_open_info_mail) return '이미 열린 신규문의 태스크가 있어 재문의로만 만들 수 있습니다.';
        if (status.has_had_info_mail) return '이전에 신규문의가 있었던 학생이라 재문의로 골라 두었습니다.';
        return '안내 메일을 받은 적 없는 잠재 학생이라 신규문의로 골라 두었습니다.';
    };

    /** Run one task request; on success report it, tell the parent, close. */
    const run = async (request, okMessage, failMessage) => {
        setBusy(true);
        try {
            await request();
            enqueueSnackbar(okMessage, { variant: 'success' });
            if (onChanged) onChanged();
            close();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || error?.response?.data?.error || failMessage, { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const searchRollback = async () => {
        try {
            const res = await Axios.get(`${base}/rollback`, auth);
            if (res.data?.message === 'No rollback found!') {
                enqueueSnackbar('No rollbacks available for this task', { variant: 'error' });
            } else {
                setRollbackTask(res.data.task);
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to search for rollbacks', { variant: 'error' });
        }
    };

    // What 태스크 생성 does. A second 신규문의 for a family that already received the 안내 메일 is asked about first: the
    // delay emails go out to the parent again (and the server puts the record back to 미발송), so the admin either
    // confirms or switches to 재문의. The status endpoint says whether the mail actually went out (has_sent_info_mail).
    const wantsInfoMail = askKind && kind === 'new';
    const createTask = () => run(
        () => Axios.post(base, { is_info_mail: wantsInfoMail }, auth),
        wantsInfoMail ? '신규문의 (안내 메일) 태스크를 만들었습니다' : '재문의 태스크를 만들었습니다',
        'Failed to create task',
    );
    const onCreateClick = () => (wantsInfoMail && status?.has_sent_info_mail ? setConfirmResend(true) : createTask());

    const tooltip = openTask?.claimed_by ? `Assigned to: ${openTask.claimed_by}` : openTask ? 'Unassigned' : 'No task created';
    return (
        <>
            <Tooltip title={tooltip}>
                <span>
                    <CancelScheduleSendIcon
                        fontSize="small"
                        color={taskBadgeColor(tasks)}
                        sx={{ cursor: disabled ? 'default' : 'pointer' }}
                        onClick={disabled ? undefined : () => setOpen(true)}
                    />
                </span>
            </Tooltip>

            <Dialog open={open} onClose={close} disableEnforceFocus PaperProps={{ sx: { minWidth: { xs: 'auto', sm: 500 }, p: 2.5 } }}>
                <DialogTitle>
                    <Typography variant="h5" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {openTask
                            ? `(${openTask.is_info_mail ? '신규문의' : '재문의'}) This memo is currently registered as a task.`
                            : 'Would you like to create a new task for this memo?'}
                    </Typography>
                    {openTask && (
                        <Typography variant="h6" sx={{ fontStyle: 'italic', opacity: 0.9 }}>
                            Assigned to: {openTask.claimed_by || 'N/A'}
                        </Typography>
                    )}
                    <Divider />
                    {!openTask && !rollbackTask && (
                        <Box sx={{ mt: 2 }}>
                            {askKind && (
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={kind}
                                    onChange={(e, v) => v && setKind(v)}
                                    disabled={busy}
                                >
                                    <Tooltip title={status?.has_open_info_mail ? '이미 열린 신규문의 태스크가 있습니다' : ''}>
                                        <span>
                                            <ToggleButton value="new" disabled={Boolean(status?.has_open_info_mail)} sx={{ textTransform: 'none', px: 1.5 }}>
                                                신규문의 (안내 메일)
                                            </ToggleButton>
                                        </span>
                                    </Tooltip>
                                    <ToggleButton value="repeat" sx={{ textTransform: 'none', px: 1.5 }}>재문의</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{hintFor()}</Typography>
                            {/* The nightly reminder job (server helpers/task_email_helper.js, 10:30 PM New York) picks up every open
                                task wherever it was made, so a task from this badge sends the same delay emails as one from 상담진행상황. */}
                            <Alert severity="info" sx={{ mt: 1.5 }}>
                                태스크를 만들면 상담진행상황에서 만든 태스크와 똑같이 학부모 이메일로 딜레이 메일이 자동 발송됩니다.
                                1차는 오늘 밤 10:30 (그 이후에 만들면 다음 날 밤), 2차는 7일 뒤 밤 10:30 (뉴욕 시간). 그 전에 태스크를 완료하면 보내지지 않습니다.
                            </Alert>
                        </Box>
                    )}
                    {rollbackTask && (
                        <Box sx={{ mt: 2.5, p: 2, backgroundColor: colors.primary[900], borderRadius: 2, border: `1px solid ${colors.grey[800]}` }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>Available Rollback:</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box>
                                    <Typography variant="body1">안내타입: {rollbackTask.is_info_mail ? '신규문의' : '재문의'}</Typography>
                                    <Typography variant="body1">Completed at: {dayjs(rollbackTask.completed_at).format('YYYY-MM-DD HH:mm')}</Typography>
                                    <Typography variant="body1">Completed by: {rollbackTask.completed_by}</Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    disabled={busy}
                                    onClick={() => run(() => Axios.put(`${base}/${rollbackTask.id}/rollback`, { is_completed: false }, auth), 'Task rolled back successfully!', 'Failed to rollback task')}
                                >
                                    Rollback
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {openTask && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2.5, p: 2, backgroundColor: colors.primary[900], borderRadius: 2, border: `1px solid ${colors.grey[800]}` }}>
                            <Line label="Requested:">{nyTime(openTask.requested_at)}</Line>
                            <Line
                                label="First Reminder:"
                                color={openTask.first_email_sent_at ? colors.primary[500] : colors.grey[400]}
                                italic={!openTask.first_email_sent_at}
                            >
                                {openTask.first_email_sent_at
                                    ? `Sent at ${nyTime(openTask.first_email_sent_at)}`
                                    : openTask.first_email_scheduled === false ? 'Cancelled' : `(Scheduled) ${firstReminderAt(openTask.requested_at)}`}
                            </Line>
                            <Line
                                label="Second Reminder:"
                                color={openTask.second_email_sent_at ? colors.primary[500] : colors.grey[400]}
                                italic={!openTask.second_email_sent_at}
                            >
                                {openTask.second_email_sent_at
                                    ? `Sent at ${nyTime(openTask.second_email_sent_at, 'YYYY-MM-DD hh:mm A [EST]')}`
                                    : openTask.second_email_scheduled === false ? 'Cancelled' : `(Scheduled) ${secondReminderAt(openTask.requested_at)}`}
                            </Line>
                        </Box>
                    )}
                </DialogTitle>
                <DialogActions sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 2, gap: 2, alignItems: 'stretch' }}>
                    {openTask ? (
                        <>
                            {openTask.claimed_by === currentUser ? (
                                <Button
                                    variant="contained"
                                    disabled={busy}
                                    onClick={() => run(() => Axios.put(`${base}/${openTask.id}/unclaim`, {}, auth), 'Task unassigned successfully!', 'Failed to unassign task')}
                                >
                                    Unassign
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    disabled={busy || openTask.claimed_by != null}
                                    onClick={() => run(() => Axios.put(`${base}/${openTask.id}/claim`, {}, auth), 'Task assigned successfully!', 'Failed to assign task')}
                                >
                                    Assign to me
                                </Button>
                            )}
                            <Button color="success" variant="contained" disabled={busy} onClick={() => setConfirmComplete(true)} sx={{ ml: '0 !important' }}>
                                Mark as Complete
                            </Button>
                            <Button color="error" variant="contained" onClick={close} sx={{ ml: '0 !important' }}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            <Button color="warning" variant="contained" disabled={busy || rollbackTask !== null} onClick={searchRollback}>
                                Search for rollbacks
                            </Button>
                            <Button
                                color="success"
                                variant="contained"
                                disabled={busy || !status || !kind}
                                sx={{ ml: '0 !important' }}
                                onClick={onCreateClick}
                            >
                                태스크 생성
                            </Button>
                            <Button color="error" variant="contained" onClick={close} sx={{ ml: '0 !important' }}>Cancel</Button>
                        </>
                    )}
                </DialogActions>

                <Dialog open={confirmComplete} onClose={() => setConfirmComplete(false)} disableEnforceFocus>
                    <DialogTitle>Are you sure you want to mark this task as complete?</DialogTitle>
                    <DialogActions>
                        <Button
                            color="success"
                            disabled={busy}
                            onClick={() => run(() => Axios.put(`${base}/${openTask.id}`, { is_completed: true }, auth), 'Task marked as complete!', 'Failed to complete task')}
                        >
                            Yes
                        </Button>
                        <Button color="error" onClick={() => setConfirmComplete(false)}>No</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmResend} onClose={() => setConfirmResend(false)} disableEnforceFocus>
                    {/* keep-all: wrap Korean at spaces only, so 계속할까요? never splits across lines */}
                    <DialogTitle sx={{ wordBreak: 'keep-all' }}>안내 메일을 이미 보낸 학생입니다. 신규문의로 만들면 딜레이 메일이 학부모에게 다시 나갑니다. 계속할까요?</DialogTitle>
                    <DialogActions>
                        <Button color="warning" disabled={busy} onClick={() => { setConfirmResend(false); createTask(); }}>계속</Button>
                        <Button variant="contained" onClick={() => { setKind('repeat'); setConfirmResend(false); }}>재문의로 바꾸기</Button>
                        <Button color="error" onClick={() => setConfirmResend(false)}>취소</Button>
                    </DialogActions>
                </Dialog>
            </Dialog>
        </>
    );
};

export default MemoTaskBadge;
